import { useState, useRef, useEffect } from "react";
import type { FileItem } from "@titancloud/shared";
import { formatBytes } from "../../utils/formatBytes";
import { formatRelativeDate } from "../../utils/dateFormat";
import { getMimeIcon } from "../../utils/mimeIcons";
import { useExplorerStore } from "../../store/explorerStore";
import { useThumbnailUrl } from "../../hooks/useThumbnailUrl";

interface FileRowProps {
  file: FileItem;
  isRenaming: boolean;
  onRenameComplete: (name: string) => void;
  onRenameCancel: () => void;
  onOpen: () => void;
  readOnly?: boolean;
  explorerUserId?: string;
}

const ICON_COLORS: Record<string, string> = {
  image: "text-pink-400",
  video: "text-purple-400",
  audio: "text-indigo-400",
  pdf: "text-red-400",
  code: "text-green-400",
  archive: "text-yellow-400",
  document: "text-blue-400",
  spreadsheet: "text-emerald-400",
  presentation: "text-orange-400",
  generic: "text-gray-400",
};

function splitFilename(name: string): { base: string; ext: string } {
  const dot = name.lastIndexOf(".");
  if (dot > 0 && dot < name.length - 1) {
    return { base: name.slice(0, dot), ext: name.slice(dot) };
  }
  return { base: name, ext: "" };
}

const LONG_PRESS_MS = 500;

export function FileRow({
  file,
  isRenaming,
  onRenameComplete,
  onRenameCancel,
  onOpen,
  readOnly,
  explorerUserId,
}: FileRowProps) {
  const [renameValue, setRenameValue] = useState(file.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didLongPress = useRef(false);

  const { selectedIds, toggleSelect, selectionMode, enterSelectionMode } = useExplorerStore();
  const isSelected = selectedIds.has(file.id);

  const iconType = getMimeIcon(file.mimeType, file.extension);
  const isMedia = iconType === "image" || iconType === "video";

  // The list endpoint already presigns thumbnails for media files; the hook is
  // only a fallback for sources (e.g. deleted-files) that haven't enriched the item.
  const needsThumbnailFetch = isMedia && file.thumbnailUrl === undefined;
  const fetchedThumbnailUrl = useThumbnailUrl(file.id, needsThumbnailFetch, explorerUserId);
  const thumbnailUrl = file.thumbnailUrl ?? fetchedThumbnailUrl;

  const [thumbnailError, setThumbnailError] = useState(false);
  useEffect(() => { setThumbnailError(false); }, [thumbnailUrl]);
  const { base, ext } = splitFilename(file.name);

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isRenaming]);

  const handleRenameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && renameValue.trim()) onRenameComplete(renameValue.trim());
    else if (e.key === "Escape") onRenameCancel();
  };

  // Long press → enter selection mode on mobile
  const startLongPress = () => {
    if (readOnly) return;
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      enterSelectionMode();
      toggleSelect(file.id);
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  };

  const handleClick = () => {
    if (isRenaming) return;
    if (didLongPress.current) { didLongPress.current = false; return; }
    if (readOnly) {
      onOpen();
      return;
    }
    if (selectionMode) {
      toggleSelect(file.id);
    } else {
      onOpen();
    }
  };

  // Checkbox visible: always in selectionMode, or on desktop hover, or when selected
  const checkboxVisible = !readOnly && (selectionMode || isSelected);

  return (
    <div
      className={`group flex items-center gap-3 border-b border-gray-800/50 px-4 py-2.5 text-sm transition-colors cursor-pointer select-none ${
        isSelected ? "bg-brand-900/30" : "hover:bg-gray-800/50"
      }`}
      onClick={handleClick}
      onTouchStart={startLongPress}
      onTouchEnd={cancelLongPress}
      onTouchCancel={cancelLongPress}
      onTouchMove={cancelLongPress}
    >
      {readOnly ? (
        <div className="h-4 w-4 flex-shrink-0" aria-hidden />
      ) : (
        <div
          className={`flex-shrink-0 transition-opacity ${
            checkboxVisible ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          onClick={(e) => { e.stopPropagation(); toggleSelect(file.id); }}
        >
          <div className={`flex h-4 w-4 items-center justify-center rounded border ${
            isSelected ? "border-indigo-500 bg-indigo-500" : "border-gray-600 bg-transparent"
          }`}>
            {isSelected && (
              <svg className="h-2.5 w-2.5 text-white" viewBox="0 0 10 8" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M1 4l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Icon or thumbnail */}
      {thumbnailUrl && !thumbnailError ? (
        <img
          src={thumbnailUrl}
          alt={file.name}
          loading="lazy"
          decoding="async"
          className="h-8 w-8 flex-shrink-0 rounded object-cover"
          onError={() => setThumbnailError(true)}
        />
      ) : (
        <svg className={`h-5 w-5 flex-shrink-0 ${ICON_COLORS[iconType] ?? "text-gray-400"}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
        </svg>
      )}

      {/* Name + mobile metadata */}
      <div className="min-w-0 flex-1">
        {isRenaming ? (
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={handleRenameKeyDown}
            onBlur={onRenameCancel}
            className="w-full rounded border border-brand-500 bg-gray-800 px-2 py-0.5 text-sm text-gray-100 focus:outline-none"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            {/* base truncates; ext is flex-shrink-0 so extension always shows */}
            <div className="flex min-w-0 items-baseline" title={file.name}>
              <span className="truncate text-gray-200 hover:text-white">{base}</span>
              {ext && <span className="flex-shrink-0 text-gray-200 hover:text-white">{ext}</span>}
            </div>
            {/* Mobile: size · date as secondary line */}
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 sm:hidden">
              <span>{formatBytes(file.sizeBytes)}</span>
              <span className="text-gray-700">·</span>
              <span>{formatRelativeDate(file.createdAt)}</span>
            </div>
          </>
        )}
      </div>

      {/* Desktop columns */}
      <div className="hidden w-24 flex-shrink-0 text-right text-gray-500 sm:block">
        {formatBytes(file.sizeBytes)}
      </div>
      <div className="hidden w-36 flex-shrink-0 text-right text-gray-500 sm:block">
        {formatRelativeDate(file.createdAt)}
      </div>
      <div className="w-8 flex-shrink-0" />
    </div>
  );
}
